import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CalendarDays, CheckCircle2, Clock3, Mail, Palmtree, XCircle } from "lucide-react";
import api from "../utils/api";
import { useUser } from "../context/UserContext";

const STATUS = {
  PRESENT: "PRESENT",
  ABSENT: "ABSENT",
  HALF_DAY: "HALF_DAY",
  HOLIDAY: "HOLIDAY",
  UNMARKED: "UNMARKED",
  EARNED_LEAVE: "EARNED_LEAVE",
  SICK_LEAVE: "SICK_LEAVE",
  COMP_OFF_LEAVE: "COMP_OFF_LEAVE",
  LATE: "LATE",
  EARLYOUT: "EARLYOUT",
};

function isPresentLike(status) {
  return status === STATUS.PRESENT || status === STATUS.LATE || status === STATUS.EARLYOUT;
}

function SimpleTimesheet() {
  const { currentUser } = useUser();
  const [selectedMonth, setSelectedMonth] = useState(startOfCurrentMonth());
  const [entries, setEntries] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const monthKey = toMonthKey(selectedMonth);
  const canGoNext = monthKey < toMonthKey(startOfCurrentMonth());

  const summary = useMemo(() => {
    return entries.reduce(
      (counts, entry) => {
        if (isPresentLike(entry.status)) counts.present += 1;
        else if (entry.status === STATUS.ABSENT) counts.absent += 1;
        else if (entry.status === STATUS.HALF_DAY) {
          counts.present += 0.5;
          counts.absent += 0.5;
          counts.halfDay += 1;
        }
        else if (entry.status === STATUS.HOLIDAY) counts.holiday += 1;
        else if (entry.locked || isLeaveStatus(entry.status)) counts.leave += 1;
        else counts.unmarked += 1;
        return counts;
      },
      { present: 0, absent: 0, halfDay: 0, holiday: 0, leave: 0, unmarked: 0 }
    );
  }, [entries]);

  useEffect(() => {
    fetchMonth();
    if (currentUser?.office !== "SPC") {
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey, currentUser?.office]);

  const fetchMonth = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/simple-timesheet/my", {
        params: { month: monthKey },
      });
      const data = response.data || [];
      const normalizedData = data.map((entry) => ({
        ...entry,
        status: entry.status ? entry.status.toUpperCase() : entry.status,
      }));
      setEntries(normalizedData);
    } catch (error) {
      toast.error("Failed to load timesheet");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await api.get("/api/simple-timesheet/my/submissions");
      setSubmissions(response.data || []);
    } catch (error) {
      setSubmissions([]);
    }
  };

  const handleMark = async (date, status) => {
    if (isFutureDate(date)) return;

    const previousEntries = entries;
    setSavingDate(date);
    setEntries((current) =>
      current.map((entry) => (entry.date === date ? { ...entry, status } : entry))
    );

    try {
      await api.post("/api/simple-timesheet/my/mark", { date, status });
    } catch (error) {
      setEntries(previousEntries);
      toast.error("Could not save that day");
    } finally {
      setSavingDate("");
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await api.post("/api/simple-timesheet/my/submit", {
        month: monthKey,
      });
      toast.success("Timesheet sent");
      fetchSubmissions();
    } catch (error) {
      toast.error("Could not send timesheet");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-3 py-5 sm:px-6">
      <div className="mx-auto w-full  space-y-6">
        <div className="flex flex-col gap-5 rounded-2xl border border-blue-200 bg-blue-100 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-blue-900 p-3 text-white shadow-sm">
              <CalendarDays size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-950">Timesheet</h1>
              <p className="text-sm text-slate-600">Mark holiday, present, half day, or absent for each day.</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-full border border-blue-200 bg-white px-3 py-2 shadow-sm sm:justify-start">
            <button
              type="button"
              onClick={() => setSelectedMonth(addMonths(selectedMonth, -1))}
              className="rounded-full px-3 py-2 text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Previous month"
            >
              ◀
            </button>
            <span className="min-w-36 text-center font-semibold text-slate-800">
              {formatMonth(selectedMonth)}
            </span>
            <button
              type="button"
              onClick={() => canGoNext && setSelectedMonth(addMonths(selectedMonth, 1))}
              className={`rounded-full px-3 py-2 transition-colors ${canGoNext ? "text-slate-600 hover:bg-slate-100" : "cursor-not-allowed text-slate-300"
                }`}
              aria-label="Next month"
              disabled={!canGoNext}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-6">
          <SummaryCard label="Holiday" value={summary.holiday} className="text-blue-700" />
          <SummaryCard label="Present" value={formatCount(summary.present)} className="text-green-700" />
          <SummaryCard label="Half Day" value={summary.halfDay} className="text-amber-700" />
          <SummaryCard label="Absent" value={formatCount(summary.absent)} className="text-red-700" />
          <SummaryCard label="Leave" value={summary.leave} className="text-violet-700" />
          <SummaryCard label="Unmarked" value={summary.unmarked} className="text-slate-600" />
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-md">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-semibold text-slate-800 sm:hidden">Monthly Entries</div>
            <div className="hidden font-semibold text-slate-800 sm:block">
              {summary.holiday} Holiday · {formatCount(summary.present)} Present · {summary.halfDay} Half Day · {formatCount(summary.absent)} Absent · {summary.leave} Leave · {summary.unmarked} Unmarked
            </div>
            {currentUser?.office !== "SPC" && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-800 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                <Mail size={17} />
                {submitting ? "Sending..." : "Submit"}
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex h-48 items-center justify-center text-slate-500">Loading timesheet...</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {entries.map((entry) => {
                const isSpc = currentUser?.office === "SPC";
                const disabled = isFutureDate(entry.date) || isSpc;
                const isSaving = savingDate === entry.date;

                return (
                  <div
                    key={entry.date}
                    className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${disabled ? "bg-slate-50 text-slate-400" : "text-slate-700"
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-lg font-bold text-slate-800">
                        {entry.date.slice(-2)}
                      </div>
                      <div>
                        <div className="font-semibold">{formatDisplayDate(entry.date)}</div>
                        <div className="text-sm text-slate-500">{formatDayName(entry.dayName)}</div>
                      </div>
                    </div>

                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                      {entry.locked || isLeaveStatus(entry.status) ? (
                        <LeaveBadge label={entry.label || leaveLabel(entry.status)} />
                      ) : (
                        <>
                          {entry.holiday && (
                            <StatusButton
                              active={entry.status === STATUS.HOLIDAY}
                              disabled={disabled || isSaving}
                              color="blue"
                              icon={<Palmtree size={17} />}
                              label="Holiday"
                              onClick={() => handleMark(entry.date, STATUS.HOLIDAY)}
                            />
                          )}
                          <StatusButton
                            active={isPresentLike(entry.status)}
                            disabled={disabled || isSaving}
                            color={entry.holiday && isPresentLike(entry.status) ? "orange" : "green"}
                            icon={<CheckCircle2 size={17} />}
                            label="Present"
                            onClick={() => handleMark(entry.date, STATUS.PRESENT)}
                          />
                          <StatusButton
                            active={entry.status === STATUS.HALF_DAY}
                            disabled={disabled || isSaving}
                            color="amber"
                            icon={<Clock3 size={17} />}
                            label="Half Day"
                            onClick={() => handleMark(entry.date, STATUS.HALF_DAY)}
                          />
                          <StatusButton
                            active={entry.status === STATUS.ABSENT}
                            disabled={disabled || isSaving}
                            color="red"
                            icon={<XCircle size={17} />}
                            label="Absent"
                            onClick={() => handleMark(entry.date, STATUS.ABSENT)}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {currentUser?.office !== "SPC" && (
          <div className="rounded-2xl bg-white p-4 shadow-md">
            <h2 className="mb-3 text-lg font-bold text-slate-800">Submission History</h2>
            {submissions.length === 0 ? (
              <div className="rounded-xl bg-slate-50 px-4 py-8 text-center text-slate-500">
                No submissions yet.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {submissions.map((submission) => (
                  <div key={submission.id} className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <span className="font-semibold text-slate-800">{submission.month}</span>
                      <span className="text-slate-500"> sent to {submission.toEmail}</span>
                    </div>
                    <div className="text-slate-500">{formatSubmittedAt(submission.submittedAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, className }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm sm:rounded-2xl sm:p-5 sm:shadow-md">
      <div className="text-[11px] font-medium leading-tight text-slate-500 sm:text-sm">{label}</div>
      <div className={`mt-1 text-xl font-bold leading-none sm:text-3xl ${className}`}>{value}</div>
    </div>
  );
}

function StatusButton({ active, disabled, color, icon, label, onClick }) {
  const activeClass = {
    blue: "border-blue-600 bg-blue-100 text-blue-700",
    green: "border-green-600 bg-green-100 text-green-700",
    amber: "border-amber-500 bg-amber-100 text-amber-700",
    orange: "border-orange-600 bg-orange-100 text-orange-700",
    red: "border-red-600 bg-red-100 text-red-700",
  }[color];

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex min-w-0 flex-1 basis-[calc(50%-0.25rem)] items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold transition sm:min-w-28 sm:flex-none sm:basis-auto sm:py-2 ${active ? activeClass : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      {icon}
      {label}
    </button>
  );
}

function LeaveBadge({ label }) {
  return (
    <div className="inline-flex min-w-36 items-center justify-center rounded-full border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700">
      {label || "Leave"}
    </div>
  );
}

function isLeaveStatus(status) {
  return status === STATUS.EARNED_LEAVE || status === STATUS.SICK_LEAVE || status === STATUS.COMP_OFF_LEAVE;
}

function leaveLabel(status) {
  if (status === STATUS.EARNED_LEAVE) return "Earned Leave";
  if (status === STATUS.SICK_LEAVE) return "Sick Leave";
  if (status === STATUS.COMP_OFF_LEAVE) return "Comp Off Leave";
  return "Leave";
}

function formatCount(value) {
  return Number.isInteger(value) ? value : value.toFixed(1);
}

function startOfCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function toMonthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonth(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function isFutureDate(dateString) {
  const today = new Date();
  const todayKey = toDateKey(today);
  return dateString > todayKey;
}

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDisplayDate(dateString) {
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
}

function formatDayName(dayName) {
  if (!dayName) return "";
  const normalized = dayName.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatSubmittedAt(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default SimpleTimesheet;
